import React, { useState, useCallback } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import api from '../../../utils/api'
import MultiSelectWithCheckBox, { type MultiSelectWithCheckBoxProps } from '../MultiSelectWithCheckBox'

interface MultiSelectApiAutoCompleteProps<T> extends Omit<MultiSelectWithCheckBoxProps<T>, 'getOptionLabel' | 'getOptionValue' | 'loading' | 'onChange' | 'options'> {
  apiEndpoint: string
  getOptionLabel: (option: T) => string
  getOptionSubLabel?: (option: T) => string
  getOptionValue: (option: T) => string
  getOptionDisabled?: (option: T) => boolean
  sortField?: string
  name: string
  apiExtraParams?: Record<string, any>
  label: string
}

export const MultiSelectApiAutoComplete = <T,>({
  apiEndpoint,
  getOptionLabel,
  getOptionSubLabel,
  getOptionValue,
  getOptionDisabled,
  name,
  apiExtraParams,
  label,
  sortField,
  ...props
}: MultiSelectApiAutoCompleteProps<T>): JSX.Element => {
  const { placeholder, disabled } = props
  const [input, setInput] = useState<string>('')

  const sortFieldUnderscore = reverseCamelCase(sortField ?? '')

  const { data, isLoading, isFetching, isPending } = useQuery<T[]>({
    queryKey: [apiEndpoint, input],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<T[]> => {
      const params: Record<string, any> = {
        pageSize: 10,
        search: input,
        ...apiExtraParams
      }
      // Add the sort field to the params only if sortField is provided
      if (sortField) {
        params.sort = JSON.stringify({
          field: sortFieldUnderscore,
          direction: 'asc'
        })
      }

      const response = await api.get(apiEndpoint, { params })
      return response.data
    }
  })

  const handleInputChange = useCallback((text: string) => {
    setInput(text)
  }, [])

  const handleOptionSelected = useCallback(() => {
    setInput('')
  }, [])

  return (
    <MultiSelectWithCheckBox
      label={label}
      name={name}
      options={data ?? []}
      placeholder={placeholder}
      getOptionLabel={(option: T) => getOptionLabel(option)}
      getOptionValue={(option: T) => getOptionValue(option)}
      getOptionSubLabel={(option: T) => getOptionSubLabel?.(option) ?? ''}
      getOptionDisabled={getOptionDisabled}
      loading={isLoading || isFetching || isPending}
      fullWidth
      onInputChange={handleInputChange}
      disabled={disabled}
      onOptionSelected={handleOptionSelected}
    />
  )
}

const reverseCamelCase = (str: string): string =>
  str.replace(/([A-Z])/g, '_$1').toLowerCase()

export default MultiSelectApiAutoComplete
